'use client';

import { useState } from 'react';
import { useBoolean, useSetState } from 'minimal-shared/hooks';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import {
  DataGrid,
  gridClasses,
  GridPagination,
  GridActionsCellItem,
  GridToolbarContainer,
  getGridStringOperators,
  GridToolbarQuickFilter,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
  GridToolbarExportContainer,
} from '@mui/x-data-grid';

import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';
import { paths, newSearchParams } from 'src/routes/paths';

import { useTranslate } from 'src/locales';
import { useGetPromotions } from 'src/actions/product';
import { PRODUCT_STOCK_OPTIONS, PRODUCT_PUBLISH_OPTIONS } from 'src/_mock';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';
import { EmptyContent } from 'src/components/empty-content';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { ButtonLoadMore } from 'src/components/custom-buttons';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';

import { useAuthContext } from 'src/auth/hooks';

import { ExportExcel } from '../components/grid-export-excel';
import { ProductTableToolbar } from '../product-table-toolbar';
import { ProductTableFiltersResult } from '../product-table-filters-result';
import {
  RenderCellStock,
  RenderCellPrice,
  RenderCellReview,
  RenderCellPublish,
  RenderCellProduct,
  RenderCellCreatedAt,
} from '../product-table-row';

// ----------------------------------------------------------------------

const HIDE_COLUMNS_TOGGLABLE = ['actions'];

export function ProductListView({ items, searchParams }) {
  const router = useRouter();

  const { user } = useAuthContext();

  const confirmDialog = useBoolean();

  const { t, currentLang } = useTranslate();

  const [tableData, setTableData] = useState([]);

  const [existsRows, setExistsRows] = useState(false);

  const filters = useSetState({ publish: [], stock: [] });

  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const [filterButtonEl, setFilterButtonEl] = useState(null);

  const search = newSearchParams({ country: currentLang.countryName });

  const [columnVisibilityModel, setColumnVisibilityModel] = useState([]);

  const { index, loading, products, loadingMore, pagination, setPagination, loadMoreProducts } =
    useGetPromotions(user?.id);

  const canReset = filters.state.publish.length > 0 || filters.state.stock.length > 0;

  const dataFiltered = applyFilter({ inputData: products, filters: filters.state });

  const { state: currentFiltersModel, setState: updateFilters } = useSetState({
    items,
    quickFilterExcludeHiddenColumns: false,
    quickFilterValues: searchParams.search?.split(' ') ?? [],
  });

  const updateParams = (paramName, value) => {
    const newSearch = newSearchParams({
      ...searchParams,
      country: currentLang.countryName,
      ...(value && !existsRows && { [paramName]: value }),
    });

    router.push(newSearch, { scroll: false });
  };

  const handleOnFilter = (model) => {
    updateFilters(model);
    updateParams('search', model.quickFilterValues?.join(' '));
    model.items.forEach(({ field, value }) => updateParams(field, value));
  };

  const handleDeleteRow = (id) => {
    const deleteRow = tableData.filter((row) => row.id !== id);
    toast.success(t('Successful removal'));
    setTableData(deleteRow);
  };

  const handleDeleteRows = () => {
    const deleteRows = tableData.filter((row) => !selectedRowIds.includes(row.id));
    toast.success(t('Successful removal'));
    setTableData(deleteRows);
  };

  const columns = [
    {
      field: 'name',
      headerName: t('Product'),
      minWidth: 240,
      hideable: false,
      renderCell: (params) => (
        <RenderCellProduct
          params={params}
          onViewRow={() => router.push(`${paths.product.details(params.row.id)}${search}`)}
        />
      ),
      filterOperators: getGridStringOperators().filter((operator) => operator.value === 'equals'),
    },
    {
      field: 'reviews',
      headerName: t('Reviews'),
      width: 160,
      filterable: false,
      renderCell: (params) => <RenderCellReview params={params} />,
    },
    {
      field: 'createdAt',
      headerName: t('Create at'),
      width: 160,
      renderCell: (params) => <RenderCellCreatedAt params={params} />,
      filterOperators: getGridStringOperators().filter((operator) => operator.value === 'equals'),
    },
    {
      field: 'inventoryType',
      headerName: t('Stock'),
      width: 160,
      type: 'singleSelect',
      valueOptions: PRODUCT_STOCK_OPTIONS,
      renderCell: (params) => <RenderCellStock params={params} />,
    },
    {
      field: 'priceSale',
      headerName: t('Sale price'),
      width: 140,
      editable: true,
      renderCell: (params) => <RenderCellPrice params={params} />,
      filterOperators: getGridStringOperators().filter((operator) => operator.value === 'equals'),
    },
    {
      field: 'publish',
      headerName: t('Published'),
      width: 140,
      type: 'singleSelect',
      editable: true,
      valueOptions: PRODUCT_PUBLISH_OPTIONS,
      renderCell: (params) => <RenderCellPublish params={params} />,
      filterOperators: getGridStringOperators().filter((operator) => operator.value === 'equals'),
    },
    {
      type: 'actions',
      field: 'actions',
      headerName: ' ',
      align: 'right',
      headerAlign: 'right',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      getActions: (params) => [
        <GridActionsCellItem
          showInMenu
          icon={<Iconify icon="solar:eye-bold" />}
          label={t('View')}
          onClick={() => router.push(`${paths.product.details(params.row.id)}${search}`)}
        />,
        <GridActionsCellItem
          showInMenu
          icon={<Iconify icon="solar:pen-bold" />}
          label={t('Edit')}
          onClick={() => router.push(`${paths.product.edit(params.row.id)}${search}`)}
        />,
        <GridActionsCellItem
          showInMenu
          icon={<Iconify icon="solar:trash-bin-trash-bold" />}
          label={t('Delete')}
          onClick={() => handleDeleteRow(params.row.id)}
          sx={{ color: 'error.main' }}
        />,
      ],
    },
  ];

  const getTogglableColumns = () =>
    columns
      .filter((column) => !HIDE_COLUMNS_TOGGLABLE.includes(column.field))
      .map((column) => column.field);

  const renderCustomToolbarContainer = () => (
    <CustomToolbar
      data={products}
      filters={filters}
      canReset={canReset}
      selectedRowIds={selectedRowIds}
      setFilterButtonEl={setFilterButtonEl}
      filteredResults={dataFiltered.length}
      onOpenConfirmDeleteRows={confirmDialog.onTrue}
    />
  );

  const renderCustomFooterContainer = () => (
    <Stack>
      <ButtonLoadMore
        perPage={25}
        showing={index}
        loading={loadingMore}
        total={products.length}
        onClick={loadMoreProducts}
        otherCondition={pagination.isLastPage}
        sx={{ p: 2 }}
      />
      <GridPagination />
    </Stack>
  );

  const renderConfirmDialog = () => (
    <ConfirmDialog
      title="Delete"
      open={confirmDialog.value}
      onClose={confirmDialog.onFalse}
      content={
        <>
          {t('Are you sure want to delete')} <strong> {selectedRowIds.length} </strong>{' '}
          {t('items?')}
        </>
      }
      action={
        <Button
          variant="contained"
          color="error"
          onClick={() => {
            handleDeleteRows();
            confirmDialog.onFalse();
          }}
        >
          {t('Delete')}
        </Button>
      }
    />
  );

  return (
    <Container sx={{ mb: 12 }}>
      <CustomBreadcrumbs
        heading={t('My promotions')}
        links={[
          { name: t('Promotions'), href: `${paths.promotions}${search}` },
          { name: t('List') },
        ]}
        action={
          <Button
            variant="contained"
            component={RouterLink}
            href={`${paths.product.new}${search}`}
            startIcon={<Iconify icon="mingcute:add-line" />}
          >
            {t('Create')}
          </Button>
        }
        sx={{ mt: { xs: 3, md: 5 }, mb: { xs: 3, md: 5 } }}
      />

      <Card
        sx={{
          flexGrow: { md: 1 },
          display: { md: 'flex' },
          height: { xs: 800, md: 600 },
          flexDirection: { md: 'column' },
        }}
      >
        <DataGrid
          checkboxSelection
          columns={columns}
          loading={loading}
          rows={dataFiltered}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          columnVisibilityModel={columnVisibilityModel}
          onFilterModelChange={(model) => handleOnFilter(model)}
          onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
          onRowSelectionModelChange={(newSelectionModel) => setSelectedRowIds(newSelectionModel)}
          initialState={{
            filter: { filterModel: currentFiltersModel },
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          onStateChange={(state) =>
            setExistsRows(Object.values(state.visibleRowsLookup).some((row) => row))
          }
          onPaginationModelChange={(model) =>
            setPagination({
              page: model.page,
              pageSize: model.pageSize,
              totalOrders: dataFiltered.length,
              isLastPage: model.page === Math.ceil(dataFiltered.length / model.pageSize) - 1,
            })
          }
          slots={{
            footer: renderCustomFooterContainer,
            toolbar: renderCustomToolbarContainer,
            noRowsOverlay: () => <EmptyContent />,
            noResultsOverlay: () => <EmptyContent title="No results found" />,
          }}
          slotProps={{
            toolbar: { setFilterButtonEl },
            panel: { anchorEl: filterButtonEl },
            columnsManagement: { getTogglableColumns },
          }}
          sx={{ [`& .${gridClasses.cell}`]: { alignItems: 'center', display: 'inline-flex' } }}
        />
      </Card>

      {renderConfirmDialog()}
    </Container>
  );
}

function CustomToolbar({
  data,
  filters,
  canReset,
  selectedRowIds,
  filteredResults,
  setFilterButtonEl,
  onOpenConfirmDeleteRows,
}) {
  const { t } = useTranslate();

  return (
    <>
      <GridToolbarContainer
        sx={{
          '& .MuiTextField-root': { width: 1 },
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <ProductTableToolbar
          filters={filters}
          options={{ stocks: PRODUCT_STOCK_OPTIONS, publishs: PRODUCT_PUBLISH_OPTIONS }}
        />

        <GridToolbarQuickFilter />

        <Stack
          spacing={1}
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          sx={{ width: 'auto', flexWrap: { xs: 'wrap', md: 'nowrap' } }}
        >
          {!!selectedRowIds.length && (
            <Button
              size="small"
              color="error"
              startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
              onClick={onOpenConfirmDeleteRows}
              sx={{ width: 'max-content' }}
            >
              {t('Delete')} ({selectedRowIds.length})
            </Button>
          )}
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton ref={setFilterButtonEl} />
          <GridToolbarExportContainer slotProps={{ tooltip: { open: false } }}>
            <ExportExcel data={data} />
          </GridToolbarExportContainer>
        </Stack>
      </GridToolbarContainer>

      {canReset && (
        <ProductTableFiltersResult
          filters={filters}
          totalResults={filteredResults}
          sx={{ p: 2.5, pt: 0 }}
        />
      )}
    </>
  );
}

function applyFilter({ inputData, filters }) {
  const { stock, publish } = filters;

  if (stock.length) {
    inputData = inputData.filter(({ inventoryType }) => stock.includes(inventoryType));
  }

  if (publish.length) {
    inputData = inputData.filter((product) => publish.includes(product.publish));
  }

  return inputData;
}
